import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Search, Image as ImageIcon, Link2, Save, X, Edit2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';

const CatalogInfo = ({ user }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    // Edit dialog state
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [formData, setFormData] = useState({ image_url: '', product_link: '', description: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await axios.get(`${API}/finished-goods`);
            // Sort to show products without image/link first
            const sorted = res.data.sort((a, b) => {
                const aHasInfo = a.image_url || a.product_link;
                const bHasInfo = b.image_url || b.product_link;
                if (!aHasInfo && bHasInfo) return -1;
                if (aHasInfo && !bHasInfo) return 1;
                return a.name.localeCompare(b.name);
            });
            setProducts(sorted);
        } catch (error) {
            toast.error('Failed to fetch products');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (product) => {
        setEditingProduct(product);
        setFormData({
            image_url: product.image_url || '',
            product_link: product.product_link || '',
            description: product.description || '',
        });
        setIsEditDialogOpen(true);
    };

    const handleUploadImage = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        if (!file.type.startsWith('image/')) {
            toast.error('Please upload an image file');
            return;
        }

        // Check file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size should be less than 5MB');
            return;
        }

        setIsUploading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await axios.post(`${API}/upload-image`, uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setFormData({ ...formData, image_url: res.data.image_url });
            toast.success('Image uploaded successfully');
        } catch (error) {
            toast.error('Failed to upload image');
            console.error(error);
        } finally {
            setIsUploading(false);
            // Reset input value to allow uploading the same file again if needed
            e.target.value = null;
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Need to send all required fields for update based on FinishedGoodsCreate
            const payload = {
                product_code: editingProduct.product_code,
                name: editingProduct.name,
                current_stock: editingProduct.current_stock,
                unit: editingProduct.unit,
                image_url: formData.image_url,
                product_link: formData.product_link,
                description: formData.description
            };

            const res = await axios.put(`${API}/finished-goods/${editingProduct.id}`, payload);

            setProducts(products.map(p => p.id === editingProduct.id ? res.data : p));
            toast.success('Catalog info updated successfully');
            setIsEditDialogOpen(false);
        } catch (error) {
            toast.error('Failed to update catalog info');
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    const filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase()) ||
        p.product_code.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Catalog Information</h1>
                    <p className="text-gray-500 mt-1">Manage images and product links for your catalog.</p>
                </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow-sm border border-stone-200 mb-6 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <Input
                        placeholder="Search products..."
                        className="pl-10"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="text-sm text-gray-500">
                    Showing {filteredProducts.length} of {products.length} products
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className="h-64 bg-stone-100 animate-pulse rounded-lg bg-gray-200"></div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredProducts.map(product => (
                        <div key={product.id} className="bg-white rounded-lg shadow-sm border border-stone-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                            <div className="h-48 bg-stone-50 border-b border-stone-100 flex items-center justify-center relative group">
                                {product.image_url ? (
                                    <img
                                        src={product.image_url.startsWith('http') ? product.image_url : `${process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin}${product.image_url}`}
                                        alt={product.name}
                                        className="w-full h-full object-contain p-4"
                                    />
                                ) : (
                                    <div className="text-center text-stone-400 flex flex-col items-center">
                                        <ImageIcon size={32} className="mb-2 opacity-50" />
                                        <span className="text-sm font-medium">No Image</span>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Button variant="secondary" onClick={() => handleEditClick(product)}>
                                        <Edit2 size={16} className="mr-2" />
                                        Edit Info
                                    </Button>
                                </div>
                            </div>

                            <div className="p-4 flex-1 flex flex-col">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-semibold text-gray-900 line-clamp-1" title={product.name}>{product.name}</h3>
                                    <span className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded font-mono">{product.product_code}</span>
                                </div>

                                <div className="mt-auto space-y-2 pt-4">
                                    <div className="flex items-center text-sm text-gray-600">
                                        <Link2 size={14} className="mr-2 text-gray-400" />
                                        {product.product_link ? (
                                            <a href={product.product_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate">
                                                View Product Link
                                            </a>
                                        ) : (
                                            <span className="text-gray-400 italic">No link added</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Catalog Info</DialogTitle>
                    </DialogHeader>

                    {editingProduct && (
                        <div className="space-y-4 py-4">
                            <div className="mb-4">
                                <div className="font-medium text-gray-900">{editingProduct.name}</div>
                                <div className="text-sm text-gray-500 font-mono">{editingProduct.product_code}</div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="image_url">Image</Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="image_url"
                                        placeholder="https://example.com/image.jpg or /images/..."
                                        value={formData.image_url}
                                        onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                        className="flex-1"
                                    />
                                    <div className="relative">
                                        <Input
                                            type="file"
                                            id="image_upload"
                                            accept="image/*"
                                            onChange={handleUploadImage}
                                            disabled={isUploading}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                        <Button type="button" variant="outline" disabled={isUploading} className="whitespace-nowrap w-[100px]">
                                            {isUploading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Upload'}
                                        </Button>
                                    </div>
                                </div>
                                {formData.image_url && (
                                    <div className="mt-2 h-32 bg-stone-50 border border-stone-200 rounded flex items-center justify-center p-2">
                                        <img
                                            src={formData.image_url.startsWith('http') ? formData.image_url : `${process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin}${formData.image_url}`}
                                            alt="Preview"
                                            className="max-h-full max-w-full object-contain"
                                            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                        />
                                        <div className="hidden text-sm text-red-500 items-center justify-center w-full h-full">Failed to load image</div>
                                    </div>
                                )}
                                <p className="text-xs text-gray-500">Tip: Right-click an image on the reference site and select "Copy Image Address".</p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="product_link">Product Web Link</Label>
                                <Input
                                    id="product_link"
                                    placeholder="https://example.com/product/..."
                                    value={formData.product_link}
                                    onChange={(e) => setFormData({ ...formData, product_link: e.target.value })}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="description">Short Description</Label>
                                <textarea
                                    id="description"
                                    placeholder="Brief product description for the brochure..."
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                />
                            </div>
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={isSaving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CatalogInfo;
